import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
} from "@angular/core";
import { GrayCellTemplateDirective } from "./gray-cell-template.directive";

export type GrayTableAlign = "start" | "center" | "end";

export interface GrayTableColumn<T = any> {
  key: string;
  header: string;
  width?: string;
  align?: GrayTableAlign;
  headerClass?: string;
  cellClass?: string | ((row: T) => string);
  formatter?: (value: any, row: T) => string;
}

export interface GrayTablePagination {
  enabled: boolean;
  page: number;          // 1-based
  pageSize: number;
  total: number;         // total rows on server
  pageSizeOptions?: number[];         // default [10,20,50,100]
  showPageSizeSelector?: boolean;     // default true
  rangeTextMode?: "short" | "full";   // optional
}

export interface GrayTablePageChangeEvent {
  page: number;
  pageSize: number;
}

@Component({
  selector: "app-gray-table",
  templateUrl: "./gray-table.component.html",
  styleUrls: ["./gray-table.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GrayTableComponent<T = any> implements AfterContentInit {
  @Input({ required: true }) columns: GrayTableColumn<T>[] = [];
  @Input({ required: true }) data: T[] = [];

  // UI
  @Input() striped = true;
  @Input() hover = true;
  @Input() bordered = false;
  @Input() small = false;
  @Input() responsive = true;

  // states
  @Input() loading = false;
  @Input() emptyText = "No data";

  // interactions
  @Input() clickableRows = true;
  @Output() rowClick = new EventEmitter<T>();

  // pagination (SERVER SIDE ONLY)
  @Input() pagination: GrayTablePagination | null = null;
  @Output() pageChange = new EventEmitter<GrayTablePageChangeEvent>();

  @ContentChildren(GrayCellTemplateDirective) cellTemplates!: QueryList<GrayCellTemplateDirective>;
  private templateMap = new Map<string, TemplateRef<any>>();

  ngAfterContentInit(): void {
    this.rebuildTemplateMap();
    this.cellTemplates.changes.subscribe(() => this.rebuildTemplateMap());
  }

  private rebuildTemplateMap(): void {
    this.templateMap.clear();
    for (const t of this.cellTemplates ?? []) this.templateMap.set(t.key, t.template);
  }

  getTemplate(key: string): TemplateRef<any> | null {
    return this.templateMap.get(key) ?? null;
  }

  onRowClick(row: T): void {
    if (!this.clickableRows) return;
    this.rowClick.emit(row);
  }

  getCellText(col: GrayTableColumn<T>, row: T): string {
    const value = (row as any)?.[col.key];
    if (col.formatter) return col.formatter(value, row);
    if (value == null) return "";
    return String(value);
  }

  getCellClass(col: GrayTableColumn<T>, row: T): string {
    if (!col.cellClass) return "";
    if (typeof col.cellClass === "string") return col.cellClass;
    return col.cellClass(row) ?? "";
  }

  getAlignClass(align?: GrayTableAlign): string {
    if (align === "center") return "text-center";
    if (align === "end") return "text-end";
    return "text-start";
  }

  // ---------- Pagination helpers (server) ----------

  get paginationEnabled(): boolean {
    return !!this.pagination?.enabled;
  }

  get page(): number {
    return this.pagination?.page ?? 1;
  }

  get pageSize(): number {
    return this.pagination?.pageSize ?? 20;
  }

  get total(): number {
    return this.pagination?.total ?? 0;
  }

  get pageSizeOptions(): number[] {
    return this.pagination?.pageSizeOptions ?? [10, 20, 50, 100];
  }

  get showPageSizeSelector(): boolean {
    return this.pagination?.showPageSizeSelector ?? true;
  }

  get totalPages(): number {
    const ps = this.pageSize || 1;
    return Math.max(1, Math.ceil(this.total / ps));
  }

  get rangeText(): string {
    const total = this.total;
    if (!total) return "0";

    const countOnPage = this.data?.length ?? 0;
    const start = (this.page - 1) * this.pageSize + 1;
    const end = Math.min(total, start + countOnPage - 1);

    const mode = this.pagination?.rangeTextMode ?? "full";
    if (mode === "short") return `${start}-${end}`;
    return `${start}-${end} / ${total}`;
  }

  goToPage(p: number): void {
    const next = Math.min(this.totalPages, Math.max(1, p || 1));
    if (next === this.page) return;
    this.pageChange.emit({ page: next, pageSize: this.pageSize });
  }

  prevPage(): void {
    this.goToPage(this.page - 1);
  }

  nextPage(): void {
    this.goToPage(this.page + 1);
  }

  onPageSizeChange(pageSizeStr: string): void {
    const ps = Number(pageSizeStr);
    const nextPageSize = Number.isFinite(ps) && ps > 0 ? ps : this.pageSize;
    this.pageChange.emit({ page: 1, pageSize: nextPageSize });
  }

  trackByIndex = (i: number) => i;
}
